/**
 * Tela 1 — Login (doc 09): centro da tela, logo Inttrix, e-mail, senha, botão
 * "Entrar" (turquesa), link "esqueci minha senha" e campo de segundo fator
 * quando o MFA está habilitado. Inclui o cadastro do MFA obrigatório
 * (Administrador/Financeiro — doc 19 §1) no primeiro acesso.
 *
 * ADR-007 (Entra ID): sem e-mail e senha no HUB — "Entrar com a conta Microsoft 365" leva ao tenant da INTTRIX.
 * Na volta, se o provedor não comprovou MFA, o HUB pede o código do autenticador DELE (e o cadastro, na primeira
 * vez) antes de abrir a sessão: a etapa vem de POST /api/auth/mfa/pendente, pelo desafio em cookie HttpOnly.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, chamar, ErroApi } from '../api.ts';
import { urlEntrarNoProvedor, useOpcoesAutenticacao } from '../autenticacao.ts';
import { useSessao } from '../sessao.tsx';
import { Aviso, Campo } from '../componentes/basicos.tsx';
import { agruparSegredo } from '../formatos.ts';

type Etapa =
  | { tipo: 'credenciais' }
  | { tipo: 'mfa' }
  | { tipo: 'cadastro_mfa'; segredo: string; otpauth: string };

export function TelaLogin({ avisoInicial }: { avisoInicial?: string | null }) {
  const { entrar } = useSessao();
  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'credenciais' });
  const [erro, setErro] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(avisoInicial ?? null);
  const [enviando, setEnviando] = useState(false);
  const [esqueci, setEsqueci] = useState(false);
  const opcoes = useOpcoesAutenticacao();
  const viaProvedor = opcoes?.estrategia === 'provedor';

  // Volta do provedor sem MFA comprovado: o desafio (cookie HttpOnly) diz em que etapa do MFA do HUB estamos
  useEffect(() => {
    if (!viaProvedor || opcoes?.provedor !== 'entra') return;
    chamar<{ etapa: string; segredo?: string; otpauth_url?: string }>('POST', '/api/auth/mfa/pendente', {}, { silencioso: true })
      .then((r) => setEtapa(r.etapa === 'mfa_requerido' ? { tipo: 'mfa' } : { tipo: 'cadastro_mfa', segredo: r.segredo!, otpauth: r.otpauth_url! }))
      .catch(() => undefined); // sem desafio: só o botão do provedor
  }, [viaProvedor, opcoes?.provedor]);

  async function enviar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const dados = new FormData(e.currentTarget);
    setErro(null);
    setInfo(null);
    setEnviando(true);
    try {
      if (etapa.tipo === 'credenciais') {
        const r = await api.post('/api/auth/login', { email: dados.get('email'), senha: dados.get('senha') });
        // CP11: a sessão (ou o desafio do MFA) chega em cookie HttpOnly — nenhum token passa por aqui
        if (r.etapa === 'autenticado') await entrar();
        else if (r.etapa === 'mfa_requerido') setEtapa({ tipo: 'mfa' });
        else setEtapa({ tipo: 'cadastro_mfa', segredo: r.segredo, otpauth: r.otpauth_url });
      } else {
        await api.post('/api/auth/mfa/verificar', { codigo: String(dados.get('codigo') ?? '').replace(/\s/g, '') });
        await entrar();
      }
    } catch (err) {
      const e2 = err as ErroApi;
      if (e2.codigo === 'desafio_expirado') setEtapa({ tipo: 'credenciais' });
      setErro(e2.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="login-fundo">
      <form className="login-cartao" onSubmit={enviar} aria-label="Entrar no INTTRIX HUB">
        <div className="login-marca">
          <img className="logo-inttrix" src="marca/inttrix-logo-horizontal.png" alt="INTTRIX Technology" width={240} height={75} />
          <span className="nome-produto">HUB</span>
        </div>

        <Aviso tipo="info">{info}</Aviso>
        <Aviso>{erro}</Aviso>

        {etapa.tipo === 'credenciais' && viaProvedor && (
          <>
            <a className="botao-primario largo" href={urlEntrarNoProvedor()} data-teste="entrar-provedor">
              {opcoes?.provedor === 'entra' ? 'Entrar com a conta Microsoft 365' : 'Entrar'}
            </a>
            <p className="ajuda">Use a sua conta corporativa da Inttrix. Se o Microsoft 365 não confirmar a verificação em duas etapas, o HUB pedirá o código do autenticador dele.</p>
          </>
        )}

        {etapa.tipo === 'credenciais' && opcoes && !viaProvedor && (
          <>
            <Campo rotulo="E-mail">
              {(id) => <input id={id} name="email" type="email" autoComplete="username" required autoFocus />}
            </Campo>
            <Campo rotulo="Senha">
              {(id) => <input id={id} name="senha" type="password" autoComplete="current-password" required />}
            </Campo>
            <button className="botao-primario largo" type="submit" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
            <button type="button" className="link-botao" onClick={() => setEsqueci((v) => !v)}>
              Esqueci minha senha
            </button>
            {esqueci && (
              <Aviso tipo="info">
                Nesta fase, a redefinição de senha é feita pelo Administrador do sistema (Administração → Usuários). A recuperação
                automática por e-mail depende da integração de e-mail prevista para o MVP 4.
              </Aviso>
            )}
          </>
        )}

        {etapa.tipo === 'cadastro_mfa' && (
          <div className="mfa-cadastro">
            <h2>Configure a verificação em duas etapas</h2>
            <p>
              {viaProvedor ? 'O HUB exige verificação em duas etapas em toda entrada.' : 'Seu perfil exige autenticação multifator.'} Abra um aplicativo autenticador (Google Authenticator, Microsoft
              Authenticator, Authy…), adicione uma conta manualmente com a chave abaixo e informe o código de 6 dígitos gerado.
            </p>
            <div className="segredo" aria-label="Chave do autenticador">
              {agruparSegredo(etapa.segredo)}
            </div>
            <p className="ajuda">
              No celular, você também pode tocar em{' '}
              <a className="link" href={etapa.otpauth}>
                abrir no aplicativo autenticador
              </a>
              .
            </p>
          </div>
        )}

        {etapa.tipo !== 'credenciais' && (
          <>
            <Campo rotulo="Código de verificação (6 dígitos)">
              {(id) => (
                <input id={id} name="codigo" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus />
              )}
            </Campo>
            <button className="botao-primario largo" type="submit" disabled={enviando}>
              {enviando ? 'Verificando…' : etapa.tipo === 'cadastro_mfa' ? 'Ativar e entrar' : 'Verificar'}
            </button>
            <button type="button" className="link-botao" onClick={() => setEtapa({ tipo: 'credenciais' })}>
              Voltar
            </button>
          </>
        )}
      </form>
    </div>
  );
}
